/**
 * The contact form's contract, shared by the React form in `src/components`
 * and the Worker in `worker/index.ts`. Keeping the field names, limits, and
 * validation in one place means the client cannot disagree with the server
 * about what a valid submission looks like.
 */

export const CONTACT_ENDPOINT = "/api/contact";

export const CONTACT_TOPICS = [
  "Full-time role",
  "Contract or freelance work",
  "Collaboration on a game",
  "Something else",
] as const;

export type ContactTopic = (typeof CONTACT_TOPICS)[number];

export const CONTACT_LIMITS = {
  name: { min: 2, max: 100 },
  email: { min: 5, max: 200 },
  company: { min: 0, max: 120 },
  message: { min: 10, max: 4000 },
} as const;

/**
 * A decoy field. It is hidden from people and from screen readers, so anything
 * that fills it in is automated.
 */
export const HONEYPOT_FIELD = "website";

/** Nobody writes a genuine message this fast; anything quicker is a script. */
export const MIN_FILL_MS = 3000;

/**
 * How long the form stays locked after a successful send. Matches the Worker's
 * per-IP rate limit window, so the button never invites a request that would
 * be turned away anyway.
 */
export const COOLDOWN_MS = 60_000;

export interface ContactFields {
  name: string;
  email: string;
  company: string;
  topic: ContactTopic;
  message: string;
}

export interface ContactSubmission extends ContactFields {
  /** Milliseconds between the form mounting and the submit. */
  elapsedMs: number;
  /** The honeypot. Always empty when a person submits. */
  website: string;
}

export type ContactValidation =
  | { ok: true; value: ContactFields }
  | { ok: false; field: keyof ContactFields; error: string };

/** Deliberately permissive — the address only has to be plausibly deliverable. */
const EMAIL_PATTERN = /^[^\s@,;:<>"']+@[^\s@.,;:<>"']+(\.[^\s@.,;:<>"']+)+$/;

const NEWLINE = 10;
const CARRIAGE_RETURN = 13;
const SPACE = 32;
const DELETE = 127;

/**
 * Strips the control characters that could otherwise forge a mail header,
 * optionally keeping newlines so the message body survives intact. Written as
 * a scan rather than a regex so the character range stays readable.
 */
function clean(value: unknown, keepNewlines: boolean): string {
  if (typeof value !== "string") return "";

  let out = "";
  let pendingReturn = false;

  for (const char of value) {
    const code = char.codePointAt(0) ?? 0;

    if (keepNewlines && (code === NEWLINE || code === CARRIAGE_RETURN)) {
      // Collapses CRLF into the single newline the mail body wants.
      if (!(code === NEWLINE && pendingReturn)) out += "\n";
      pendingReturn = code === CARRIAGE_RETURN;
      continue;
    }

    pendingReturn = false;
    if (code < SPACE || code === DELETE) {
      if (!keepNewlines) out += " ";
      continue;
    }
    out += char;
  }

  return out.trim();
}

const singleLine = (value: unknown) => clean(value, false);
const multiLine = (value: unknown) => clean(value, true);

/**
 * Normalises and checks one submission. Returns the cleaned fields, or the
 * first field that failed and a message safe to show the visitor.
 */
export function validateContact(input: unknown): ContactValidation {
  const raw = (input ?? {}) as Record<string, unknown>;

  const name = singleLine(raw.name);
  if (name.length < CONTACT_LIMITS.name.min) {
    return { ok: false, field: "name", error: "Please add your name." };
  }
  if (name.length > CONTACT_LIMITS.name.max) {
    return { ok: false, field: "name", error: "That name is too long." };
  }

  const email = singleLine(raw.email).toLowerCase();
  if (!EMAIL_PATTERN.test(email) || email.length > CONTACT_LIMITS.email.max) {
    return {
      ok: false,
      field: "email",
      error: "Please check your email address.",
    };
  }

  const company = singleLine(raw.company);
  if (company.length > CONTACT_LIMITS.company.max) {
    return {
      ok: false,
      field: "company",
      error: "That company name is too long.",
    };
  }

  const topic = singleLine(raw.topic) as ContactTopic;
  if (!CONTACT_TOPICS.includes(topic)) {
    return { ok: false, field: "topic", error: "Please pick a topic." };
  }

  const message = multiLine(raw.message);
  if (message.length < CONTACT_LIMITS.message.min) {
    return {
      ok: false,
      field: "message",
      error: "Please add a few more lines.",
    };
  }
  if (message.length > CONTACT_LIMITS.message.max) {
    return {
      ok: false,
      field: "message",
      error: `Please keep the message under ${CONTACT_LIMITS.message.max} characters.`,
    };
  }

  return { ok: true, value: { name, email, company, topic, message } };
}
